import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp, 
  orderBy, 
  where,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../../firebase';

const CareerManagement = () => {
  const [activeTab, setActiveTab] = useState('positions');
  const [positions, setPositions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [loadingApplication, setLoadingApplication] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMorePages, setHasMorePages] = useState(true);

  // Position form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    type: 'Full-time',
    responsibilities: [''],
    qualifications: [''],
    isActive: true
  });

  // Tab definitions
  const tabs = [
    { id: 'positions', label: 'Job Positions' },
    { id: 'applications', label: 'Applications' }
  ];

  // Load positions and applications with pagination
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === 'positions') {
          await fetchPositions();
        } else {
          await fetchApplications();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };

    fetchData();
  }, [activeTab, currentPage]);

  const fetchPositions = async () => {
    const positionsQuery = query(
      collection(db, "career_positions"),
      orderBy("postedDate", "desc"),
      limit(itemsPerPage)
    );
    const positionsSnapshot = await getDocs(positionsQuery);
    const positionsList = positionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setPositions(positionsList);
    setTotalItems(positionsSnapshot.size);
    setHasMorePages(positionsSnapshot.size === itemsPerPage);
  };

  const fetchApplications = async () => {
    const applicationsQuery = query(
      collection(db, "career_applications"),
      orderBy("appliedAt", "desc"),
      limit(itemsPerPage)
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);
    const applicationsList = applicationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setApplications(applicationsList);
    setTotalItems(applicationsSnapshot.size);
    setHasMorePages(applicationsSnapshot.size === itemsPerPage);
  };

  // Handle basic input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle array field changes (responsibilities, qualifications)
  const handleArrayFieldChange = (index, field, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  // Add new item to array field
  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  // Remove item from array field
  const removeArrayItem = (field, index) => {
    if (formData[field].length === 1) return;
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  // Reset position form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      type: 'Full-time',
      responsibilities: [''],
      qualifications: [''],
      isActive: true
    });
    setCurrentPosition(null);
    setEditMode(false);
  };

  // Edit position
  const handleEdit = (position) => {
    setCurrentPosition(position);
    setFormData({
      title: position.title || '',
      description: position.description || '',
      location: position.location || '',
      type: position.type || 'Full-time',
      responsibilities: position.responsibilities?.length ? position.responsibilities : [''],
      qualifications: position.qualifications?.length ? position.qualifications : [''],
      isActive: position.isActive !== false // Default to true if not specified
    });
    setEditMode(true);
  };

  // Delete position
  const handleDelete = async (positionId) => {
    if (!window.confirm('Are you sure you want to delete this position? This action cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, "career_positions", positionId));
      setPositions(prev => prev.filter(position => position.id !== positionId));
    } catch (error) {
      console.error("Error deleting position:", error);
    }
  };

  // Submit position form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty entries from arrays
    const responsibilities = formData.responsibilities.filter(item => item.trim());
    const qualifications = formData.qualifications.filter(item => item.trim());
    
    try {
      // Prepare position data
      const positionData = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        type: formData.type,
        responsibilities,
        qualifications,
        isActive: formData.isActive,
        updatedAt: serverTimestamp()
      };
      
      if (editMode && currentPosition) {
        // Update existing position
        await updateDoc(doc(db, "career_positions", currentPosition.id), positionData);
        
        // Update local state
        setPositions(prev => prev.map(position => 
          position.id === currentPosition.id 
            ? { ...position, ...positionData, id: currentPosition.id } 
            : position
        ));
      } else {
        // Create new position
        positionData.postedDate = serverTimestamp();
        positionData.createdAt = serverTimestamp();
        
        const docRef = await addDoc(collection(db, "career_positions"), positionData);
        
        // Update local state
        setPositions(prev => [
          { ...positionData, id: docRef.id },
          ...prev
        ]);
      }
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error("Error saving position:", error);
    }
  };

  // View application details
  const viewApplicationDetails = async (application) => {
    setSelectedApplication(application);
    setLoadingApplication(true);
    
    try {
      // Get user details
      if (application.userId) {
        const userDoc = await getDoc(doc(db, "users", application.userId));
        if (userDoc.exists()) {
          application.userDetails = userDoc.data();
        }
      }
      
      setApplicationDetails(application);
    } catch (error) {
      console.error("Error fetching application details:", error);
    }
    
    setLoadingApplication(false);
  };

  // Update application status
  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(db, "career_applications", applicationId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus, updatedAt: serverTimestamp() } 
          : app
      ));
      
      if (selectedApplication && selectedApplication.id === applicationId) {
        setSelectedApplication(prev => ({
          ...prev,
          status: newStatus,
          updatedAt: serverTimestamp()
        }));
        setApplicationDetails(prev => ({
          ...prev,
          status: newStatus,
          updatedAt: serverTimestamp()
        }));
      }
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Pagination controls
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setLastDoc(null);
    setHasMorePages(true);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Career Management</h1>
      
      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  resetPagination();
                }}
                className={`py-4 px-6 font-medium text-sm focus:outline-none ${
                  activeTab === tab.id
                    ? 'border-b-2 border-[rgb(130,88,18)] text-[rgb(130,88,18)]'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Job Positions Tab */}
      {activeTab === 'positions' && (
        <>
          {/* Position Form */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-semibold mb-4">{editMode ? 'Edit Job Position' : 'Add New Job Position'}</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    placeholder="e.g., Remote, New York, NY"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                    <option value="Volunteer">Volunteer</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                  required
                ></textarea>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Responsibilities</label>
                  <button
                    type="button"
                    onClick={() => addArrayItem('responsibilities')}
                    className="text-[rgb(130,88,18)] text-sm"
                  >
                    + Add responsibility
                  </button>
                </div>
                {formData.responsibilities.map((item, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayFieldChange(index, 'responsibilities', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      placeholder={`Responsibility ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('responsibilities', index)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-r-md hover:bg-gray-300"
                      disabled={formData.responsibilities.length === 1}
                    >
                      -
                    </button>
                  </div>
                ))}
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Qualifications</label>
                  <button
                    type="button"
                    onClick={() => addArrayItem('qualifications')}
                    className="text-[rgb(130,88,18)] text-sm"
                  >
                    + Add qualification
                  </button>
                </div>
                {formData.qualifications.map((item, index) => (
                  <div key={index} className="flex mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleArrayFieldChange(index, 'qualifications', e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      placeholder={`Qualification ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('qualifications', index)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-r-md hover:bg-gray-300"
                      disabled={formData.qualifications.length === 1}
                    >
                      -
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4 w-4 text-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)] border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Active (visible on careers page)
                </label>
              </div>
              
              <div className="flex justify-end space-x-4 pt-2">
                {editMode && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,68,0)]"
                >
                  {editMode ? 'Update Position' : 'Add Position'}
                </button>
              </div>
            </form>
          </div>
          
          {/* Positions List */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Job Positions</h2>
            
            {loading ? (
              <div className="flex justify-center my-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
              </div>
            ) : positions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Posted Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {positions.map(position => (
                      <tr key={position.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{position.title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{position.location || 'Remote'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{position.type || 'Full-time'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(position.postedDate)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            position.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {position.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleEdit(position)}
                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(position.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-4 text-gray-500">No job positions found. Add your first position above!</p>
            )}
          </div>
        </>
      )}
      
      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4">Applications</h2>
            
            {loading ? (
              <div className="flex justify-center my-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
              </div>
            ) : applications.length > 0 ? (
              <div className="space-y-4">
                {applications.map(application => (
                  <div 
                    key={application.id} 
                    className={`p-4 border rounded-md cursor-pointer transition-colors ${
                      selectedApplication?.id === application.id
                        ? 'border-[rgb(130,88,18)] bg-[rgb(130,88,18)]/10'
                        : 'border-gray-200 hover:border-[rgb(130,88,18)] hover:bg-[rgb(130,88,18)]/5'
                    }`}
                    onClick={() => viewApplicationDetails(application)}
                  >
                    <h3 className="font-medium mb-1">{application.fullName}</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      For: <span className="font-medium">{application.positionTitle}</span>
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">{formatDate(application.appliedAt)}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        application.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        application.status === 'review' ? 'bg-yellow-100 text-yellow-800' :
                        application.status === 'interview' ? 'bg-purple-100 text-purple-800' :
                        application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {application.status?.charAt(0).toUpperCase() + application.status?.slice(1) || 'Unknown'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-gray-500">No applications found.</p>
            )}
            
            {/* Applications Pagination */}
            {applications.length > 0 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {applications.length} of {totalItems} applications
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md text-sm ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  
                  <span className="text-sm text-gray-700 px-2">
                    Page {currentPage}
                  </span>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasMorePages}
                    className={`px-3 py-1 rounded-md text-sm ${
                      !hasMorePages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Application Details */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
            {selectedApplication ? (
              loadingApplication ? (
                <div className="flex justify-center my-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-xl font-semibold mb-1">{applicationDetails.fullName}</h2>
                      <p className="text-sm text-gray-600">
                        Application for <span className="font-medium">{applicationDetails.positionTitle}</span>
                      </p>
                    </div>
                    <div>
                      <select
                        value={applicationDetails.status || 'new'}
                        onChange={(e) => updateApplicationStatus(applicationDetails.id, e.target.value)}
                        className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      >
                        <option value="new">New</option>
                        <option value="review">Under Review</option>
                        <option value="interview">Interview</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Contact Information</h3>
                      <p className="mb-1"><span className="font-medium">Email:</span> {applicationDetails.email}</p>
                      <p><span className="font-medium">Phone:</span> {applicationDetails.phone}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Application Details</h3>
                      <p className="mb-1"><span className="font-medium">Applied:</span> {formatDate(applicationDetails.appliedAt)}</p>
                      <p><span className="font-medium">Status:</span> {applicationDetails.status?.charAt(0).toUpperCase() + applicationDetails.status?.slice(1) || 'New'}</p>
                    </div>
                  </div>
                  
                  {(applicationDetails.portfolioLink || applicationDetails.linkedinProfile) && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Links</h3>
                      {applicationDetails.portfolioLink && (
                        <p className="mb-1">
                          <span className="font-medium">Portfolio:</span> 
                          <a href={applicationDetails.portfolioLink} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">
                            {applicationDetails.portfolioLink}
                          </a>
                        </p>
                      )}
                      {applicationDetails.linkedinProfile && (
                        <p>
                          <span className="font-medium">LinkedIn:</span> 
                          <a href={applicationDetails.linkedinProfile} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">
                            {applicationDetails.linkedinProfile}
                          </a>
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Resume / CV</h3>
                    {applicationDetails.resumeURL ? (
                      <div className="flex items-center">
                        <a 
                          href={applicationDetails.resumeURL} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:bg-[rgb(110,68,0)] flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          Download Resume
                        </a>
                      </div>
                    ) : (
                      <p className="text-gray-500">No resume available</p>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Cover Letter</h3>
                    <div className="p-4 bg-gray-50 rounded-md">
                      <p className="whitespace-pre-line">{applicationDetails.coverLetter}</p>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-64">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500">Select an application to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerManagement; 